import React, { useState, useMemo, useEffect } from 'react';
import './style.scss';
import Icon from 'components/Utils/NewIcon';
import DefaultTextField from 'components/Utils/DefaultTextField';
import { DefaultCheckbox, DefaultSwitch } from 'services/UIUtils';
import PatientUtils from 'services/PatientUtils';
import { useAppSelector } from 'careand-redux/store';
import { BeautifyModalContent } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/BeautifyModal';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import PerformanceUtils from 'services/PerformanceUtils';
import usePersistedState from 'hooks/usePersistedState';
import CPPMedicationListElement from 'components/Patient/CPP/CPPMedicationListElement';
import AppointmentNoteScreen from 'components/HealthRecords/AppointmentNotes/AppointmentNoteScreen';
import CPPPatientMedicationInput from 'components/Patient/CPP/CPPInput/Inputs/CPPPatientMedicationInput';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultModal from 'components/Utils/DefaultModal';
import {
  FLOW_INPUTS,
  Flows,
} from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/Flow/flows';

const DEFAULT_OBJECT = {};

const Flow = ({ patient, complaint, onAccept, bubble, flow, overrideFormId }) => {
  const key = `Flow-${flow}-${patient.id}`;
  const patientHealthRecords =
    useAppSelector((state) => state.healthRecords.byId[patient.id]) || DEFAULT_OBJECT;
  const primaryPhysician = useAppSelector(
    (state) => state.physicians.byId[patient?.primary_physician],
  );
  const ownClinic = useAppSelector((state) => state.resource.clinic);
  const clinics = useAppSelector((state) => state.resource.clinics);
  const patientClinic = useMemo(() => {
    return clinics.find((clinic) => clinic.id === patient.clinic_id) || ownClinic;
  }, [clinics, patient.clinic_id, ownClinic]);
  const clinicalDirector = useAppSelector(
    (state) => state.physicians.byId[patientClinic?.clinical_director_id],
  );

  const [pageNumber, setPageNumber, deletePageNumberCache] = usePersistedState(
    `${key}.pageNumber`,
    0,
  );
  const [answers, setAnswers, deleteAnswersCache] = usePersistedState(`${key}.answers`, {}, true);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  const [filters, setFilters] = useState({
    gender: PatientUtils.getGender(patient),
  });
  const [showBeautify, setShowBeautify] = useState(false);
  const [removeOnQuit, setRemoveOnQuit] = useState(false);

  useEffect(() => {
    return () => {
      if (removeOnQuit) {
        deleteCache();
      }
    };
  }, [removeOnQuit]);

  const data = useMemo(() => {
    return {
      healthRecords: patientHealthRecords,
      patient,
      primaryPhysician,
      clinicalDirector,
      answers,
    };
  }, [patientHealthRecords, patient, primaryPhysician, clinicalDirector]);

  function deleteCache() {
    deletePageNumberCache();
    deleteAnswersCache();
  }

  const includeBeautify = Flows[flow].includeBeautify;
  const soapSections = Flows[flow].soapSections;
  const complaints = Flows[flow].complaints || complaint;
  const alwaysIncludeFirstPage = Flows[flow].alwaysIncludeFirstPage;

  const pages = useMemo(() => {
    return Flows[flow].pages(data).filter((page) => {
      let passedFilter = true;
      if (page.filters) {
        Object.keys(page.filters).forEach((key) => {
          if (filters[key] !== page.filters[key]) {
            passedFilter = false;
          }
        });
      }
      return passedFilter;
    });
  }, [filters, data, flow]);

  const page = pages[pageNumber];

  function onChange(page, index, value, filters = {}) {
    if (Object.keys(filters).length > 0) {
      setFilters((prevState) => ({ ...prevState, ...filters }));
    }
    setAnswers((prevState) => ({
      ...prevState,
      [page.title]: {
        ...(prevState[page.title] || {}),
        [index]: value,
      },
    }));
  }

  function changePage(next = true) {
    let amount = next ? 1 : -1;
    if (amount === 1 && pageNumber === pages.length - 1) {
      setShowBeautify(true);
    } else if (amount === -1 && showBeautify) {
      setShowBeautify(false);
    } else {
      setPageNumber((prevState) => Math.min(pages.length - 1, Math.max(0, prevState + amount)));
    }
  }

  function onAcceptBeautify(note, records) {
    if (records) {
      records = PerformanceUtils.clone(records);
    }
    setButtonsDisabled(true);
    setRemoveOnQuit(true);
    return onAccept(note, records, true);
  }

  const note = useMemo(() => {
    function getQuestionText(page, question, answers, index, subIndex) {
      let note = '';
      let answerValue = (answers[page.title] || {})[index];
      let answer =
        (subIndex !== undefined ? (answerValue || [])[subIndex] : answerValue) ||
        question.defaultAnswer;

      if (Array.isArray(answer)) {
        answer = answer.filter(Boolean).join(', ');
      }

      if (typeof answer === 'boolean') {
        answer = answer ? 'Yes' : 'No';
      }

      if (answer && question.noAnswerText) {
        note += `${question.label}\n`;
      } else if (answer) {
        note += `${question.noteLabel || question.label || ''}${
          subIndex !== undefined ? '' : ':'
        } ${answer}\n`;
      }
      return note;
    }

    let note = 'S/\n\n';
    if (complaints) note += `cc: "${complaints}"\n\n`;
    pages.forEach((page) => {
      let pageText = '';

      let skip = false;
      let pendingTitle = null;

      page?.questions?.forEach((question, index) => {
        if (skip) return;

        // Section headers are deferred: only emit one once a question below it
        // actually has an answer, so empty sections don't clutter the note.
        if (question.title) {
          pendingTitle = question.title;
          return;
        }

        let questionText = '';
        if (question.questions) {
          let questionsText = question.questions
            .map((subQuestion, subIndex) => {
              return getQuestionText(page, subQuestion, answers, index, subIndex).trim();
            })
            .filter((v) => v)
            .join(question.noNewLine ? ', ' : '\n');
          if (questionsText) {
            questionText = `${question.noteLabel || question.label}:${
              question.noNewLine ? ' ' : '\n'
            }${questionsText}\n`;
          }
        } else {
          questionText = getQuestionText(page, question, answers, index);
        }

        if (questionText) {
          if (pendingTitle) {
            pageText += `${pendingTitle}\n`;
            pendingTitle = null;
          }
          pageText += questionText;
        }

        if (question.skipPage && (answers[page.title] || [])[index] === true) {
          skip = true;
        }
      });

      if (pageText) {
        if (!page.hideTitle) pageText = `${page.noteTitle || `${page.title}:`}\n${pageText}`;
        note += `${pageText}\n`;
      }
    });

    if (soapSections) {
      if (soapSections.O) note += `O/\n${soapSections.O}\n\n`;
      if (soapSections.A) note += `A/\n${soapSections.A}\n\n`;
      if (soapSections.P) note += `P/\n${soapSections.P}\n\n`;
    }

    return note;
  }, [pages, answers, complaints, soapSections]);

  useEffect(() => {
    onAccept(note);
  }, [note]);

  return (
    <div className="flow-modal">
      {/*This is to make sure drafts keep getting saved*/}
      <AppointmentNoteScreen patient={patient} bubble={bubble} overrideFormId={overrideFormId} />

      {pages.length > 1 && (
        <div className="flow-nav-rail">
          {pages.map((railPage, railIndex) => (
            <div
              key={`${railPage.title}-${railIndex}`}
              className={`rail-item${!showBeautify && railIndex === pageNumber ? ' current' : ''}`}
              onClick={() => {
                setShowBeautify(false);
                setPageNumber(railIndex);
              }}
            >
              <span className="rail-index">{railIndex + 1}</span>
              <span className="rail-title">{railPage.title}</span>
            </div>
          ))}
        </div>
      )}

      {(pageNumber > 0 || showBeautify) && (
        <Navigation tabIndex={0} onNavigate={() => changePage(false)} icon="chevron-left" />
      )}
      {page && !showBeautify && (
        <div className="question-page" key={pageNumber}>
          <span className="large-title">
            {page.title} ({pageNumber + 1}/{pages.length})
          </span>
          {page.description && <span className="page-description">{page.description}</span>}
          {page.actions ? page.actions(patient) : null}
          {page.questions?.map((question, index) => (
            <Question
              key={pageNumber + question.label + index}
              patient={patient}
              healthRecords={patientHealthRecords}
              question={question}
              onChange={(value, filters) => onChange(page, index, value, filters)}
              value={(answers[page.title] || {})[index]}
              index={index}
              filters={filters}
              autoFocus={index === 0}
            />
          ))}
          {alwaysIncludeFirstPage &&
            pageNumber > 0 &&
            pages[0].questions.map((question, index) => (
              <Question
                key={0 + question.label + index}
                patient={patient}
                healthRecords={patientHealthRecords}
                question={question}
                onChange={(value, filters) => onChange(pages[0], index, value, filters)}
                value={(answers[pages[0].title] || {})[index]}
                index={index}
                filters={filters}
              />
            ))}
        </div>
      )}
      {showBeautify && (
        <div className="end-wrapper" key={pageNumber}>
          {includeBeautify
            ? note && (
                <BeautifyModalContent
                  key={patient.id}
                  patient={patient}
                  patientHr={patientHealthRecords}
                  complaint={complaint}
                  originalNote={note}
                  onAccept={onAcceptBeautify}
                  includeNote={false}
                  includeSummary={Flows[flow].summary}
                  buttonsDisabled={buttonsDisabled}
                />
              )
            : Flows[flow].endPage
            ? Flows[flow].endPage(data, () => onAccept(note, undefined, true), buttonsDisabled)
            : null}
        </div>
      )}
      {page && !showBeautify && (
        <Navigation
          tabIndex={page.questions?.length + 1}
          onNavigate={() => changePage(true)}
          icon="chevron-right"
        />
      )}
    </div>
  );
};

const Navigation = ({ onNavigate, tabIndex, icon }) => {
  const [active, setActive] = useState(false);

  let className = 'navigation';
  if (active) className += ' active';

  function onKeyDown(event) {
    event.stopPropagation();
    event.preventDefault();
    if (event.key === 'Enter') {
      onNavigate();
    }
  }

  return (
    <div className={className} onClick={onNavigate} onKeyDown={onKeyDown}>
      <Icon src={icon} height="30px" className="m-0" />
      <DefaultTextField
        className="input-focus"
        tabIndex={tabIndex}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
      />
    </div>
  );
};

const Question = ({
  question,
  value,
  onChange,
  index,
  filters,
  onFocus,
  onBlur,
  patient,
  healthRecords,
  autoFocus = false,
}) => {
  const [focused, setFocused] = useState(false);
  const [multipleAnswers, setMultipleAnswers] = useState(value || []);
  const [medicationModal, setMedicationModal] = useState(false);

  let passesFilters = true;

  const defaultAnswer = question.defaultAnswer;

  if (question.filters) {
    Object.keys(question.filters).forEach((key) => {
      if (filters[key] !== question.filters[key]) {
        passesFilters = false;
      }
    });
  }

  if (!passesFilters) return null;

  let className = 'question';
  if (focused) className += ' active';
  if (question.title) className += ' section-title';
  if (question.inline) className += ' inline-field';

  const isSwitch = question.input === FLOW_INPUTS.switch;
  const isCheckbox = question.input === FLOW_INPUTS.checkbox;

  function onChangeWrapper(value) {
    let filters = {};
    if (question.filterLabel) {
      filters = { [question.filterLabel]: value };
    }
    return onChange(value, filters);
  }

  function onFocusWrapper() {
    if (onFocus) onFocus();
    setFocused(true);
  }

  function onBlurWrapper() {
    if (onBlur) onBlur();
    setFocused(false);
  }

  return (
    <div className={className}>
      {!isSwitch && !isCheckbox && !question.hideLabel && (
        <div className="question-label">
          <span className="label">{question.label || question.title}</span>
          {question.instructions && <span className="instructions">{question.instructions}</span>}
        </div>
      )}
      {!question.questions && (question.label || question.hideLabel) && (
        <>
          {(question.input === FLOW_INPUTS.textarea || !question.input) && (
            <TextareaAutosize
              autoFocus={autoFocus}
              name={`${question?.label || ''}+${index}`}
              className={focused ? 'active-textarea' : ''}
              defaultValue={value === undefined ? defaultAnswer : value}
              value={value === undefined ? defaultAnswer : value}
              onChange={(event) => onChangeWrapper(event.target.value)}
              onFocus={onFocusWrapper}
              onBlur={onBlurWrapper}
            />
          )}
          {question.input === FLOW_INPUTS.text && (
            <DefaultTextField
              autoFocus={index === 0}
              name="question"
              value={value === undefined ? defaultAnswer : value}
              tabIndex={question.label ? `${index + 1}` : '-1'}
              onChange={(event) => onChangeWrapper(event.target.value)}
              onFocus={onFocusWrapper}
              onBlur={onBlurWrapper}
            />
          )}
          {question.input === FLOW_INPUTS.checkbox && (
            <DefaultCheckbox
              labelClassName={focused ? 'active' : ''}
              name="question"
              value={value === undefined && defaultAnswer ? defaultAnswer : value}
              onChange={() => onChangeWrapper(!(value === undefined ? defaultAnswer : value))}
              text={question.label}
              onFocus={onFocusWrapper}
              onBlur={onBlurWrapper}
            />
          )}
          {question.input === FLOW_INPUTS.medications && (
            <div className="drugs-wrapper">
              <CPPMedicationListElement
                key="medications"
                patient={patient}
                items={healthRecords?.healthRecords?.medications}
                hideInactive={true}
                extraKey="PA"
              />
              <div className="row">
                <TextareaAutosize
                  autoFocus={autoFocus}
                  name={`${question?.label || ''}+${index}`}
                  className={focused ? 'active-textarea' : ''}
                  defaultValue={value === undefined ? defaultAnswer : value}
                  value={value === undefined ? defaultAnswer : value}
                  onChange={(event) => onChangeWrapper(event.target.value)}
                  onFocus={onFocusWrapper}
                  onBlur={onBlurWrapper}
                />
                <DefaultButton
                  icon="pills"
                  name="DrugBank"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setMedicationModal(true);
                  }}
                  style={{ width: '120px' }}
                />
              </div>
              {!!medicationModal && (
                <DefaultModal
                  className="add-medication-modal"
                  title="Add Medication"
                  wrap={true}
                  padding={true}
                  draggable
                  content={
                    <CPPPatientMedicationInput
                      key="medications"
                      title="Medication"
                      field="medications"
                      onClose={() => setMedicationModal(false)}
                      patient={patient}
                    />
                  }
                  isOpen={!!medicationModal}
                  onRequestClose={() => setMedicationModal(false)}
                />
              )}
            </div>
          )}
          {question.input === FLOW_INPUTS.multiselect && (
            <div className="multiselect-chips">
              {(question.inputOptions?.options || []).map((option) => {
                const single = question.inputOptions?.single;
                const selected = (value || []).includes(option);
                const toggle = () => {
                  const current = value || [];
                  let next;
                  if (single) {
                    next = selected ? [] : [option];
                  } else {
                    next = selected
                      ? current.filter((item) => item !== option)
                      : [...current, option];
                  }
                  onChangeWrapper(next);
                };
                return (
                  <div
                    key={option}
                    className={`chip${selected ? ' selected' : ''}`}
                    role={single ? 'radio' : 'checkbox'}
                    aria-checked={selected}
                    tabIndex={0}
                    onClick={toggle}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        toggle();
                      }
                    }}
                  >
                    {option}
                  </div>
                );
              })}
            </div>
          )}
          {isSwitch && (
            <DefaultSwitch
              onChange={(event, isInputChecked) => onChangeWrapper(isInputChecked)}
              label={question.label}
              padded={false}
              onFocus={onFocusWrapper}
              onBlur={onBlurWrapper}
              checked={value === undefined ? question.inputOptions?.defaultValue || false : value}
            />
          )}
        </>
      )}
      {question.questions && (
        <div className="multiple-questions">
          {question.questions.map((q, index) => (
            <Question
              key={q.label + index}
              patient={patient}
              healthRecords={healthRecords}
              question={q}
              value={multipleAnswers[index]}
              onFocus={onFocusWrapper}
              onBlur={onBlurWrapper}
              onChange={(val) => {
                let values = [...multipleAnswers];
                values[index] = val;
                onChange(values);
                setMultipleAnswers(values);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Flow;
